import { onScreen } from './visible';

function fieldSelector(name: string): string {
  return `[data-el-field="${name}"], [data-el-field^="${name}__"]`;
}

// 「지금 화면에 있는가」는 `visible.ts` 하나가 정한다. 여기서 또 정의하면 잣대가 갈린다.
const drawn = onScreen;

/**
 * 그 이름의 검색조건 칸.
 *
 * **보이는 화면의 칸을 먼저 고른다.** 화면 둘을 잇달아 조회하면 앞 화면이 감춰진
 * 채로 남아 같은 이름의 칸이 둘이 된다. 문서 첫 번째를 집으면 안 보이는 칸을 누르게
 * 되고, 콤보는 열리지도 않아 `그 칸에는 고를 목록이 없다` 로 턴이 죽는다 —
 * 인사 보고서(R6)가 `rtclafi` 에서 그렇게 죽었다. 조용히 다른 화면에 조건을 걸어
 * 엉뚱한 답을 내는 쪽이 더 나쁘다.
 *
 * 하나도 안 보이면 첫 번째를 준다. 권한에 따라 숨긴 채 상태로만 적용되는 조건이 있다.
 */
export function findField(name: string): HTMLElement | null {
  const found = Array.from(document.querySelectorAll<HTMLElement>(fieldSelector(name)));
  // **창 안 칸을 채우는 동안에는 그 창의 칸이 먼저다.** 보수현장은 본문 폼과 담당자변경 창이 둘 다 `perid2` 를
  // 쓴다 — 첫 칸을 집어 본문 보조자 칸에 사번을 넣고, 창의 후담당자는 빈 채로 「고르지 못했다」였다(2026-10-14).
  // 늘 창을 먼저 보면 안 된다: 앱은 닫힌 창 껍데기(ModalView)도 그려 두어, 입금입력의 칸을 엉뚱하게 잡고
  // 미수를 못 골랐다(쓰기 벤치 회귀, 2026-10-14). 그래서 `withinModal` 이 연 동안만 쓴다.
  if (modalScope && found.length > 1) {
    const inModal = found.filter((one) => drawn(one) && modalScope!.contains(one));
    if (inModal.length) return inModal[inModal.length - 1]!;
  }
  return found.find(drawn) ?? found[0] ?? null;
}

let modalScope: HTMLElement | null = null;

/** 그 창 안의 칸을 채우는 동안 `findField` 가 창의 칸을 먼저 집게 한다. */
export async function withinModal<T>(modal: HTMLElement, work: () => Promise<T>): Promise<T> {
  const before = modalScope;
  modalScope = modal;
  try {
    return await work();
  } finally {
    modalScope = before;
  }
}

/** 여럿 중 하나를 고르되, `undefined` 는 `null` 로 맞춘다. */
function lastOf(pool: HTMLElement[]): HTMLElement | null {
  return pool[pool.length - 1] ?? null;
}

/**
 * 초안 본문에서 그 이름의 칸.
 *
 * 한 화면이 같은 이름을 위(조회 조건)와 아래(본문)에 다 쓰는 일이 있다 —
 * 출장보고서의 `stdate` 는 위가 조회 기간, 아래 `stdate__2` 가 출장기간이다.
 * 첫 앵커를 쓰면 초안 값이 조회 조건으로 들어가고 본문은 빈 채로 남는다.
 * 본문은 조회 조건 아래에 그려지므로 마지막 앵커가 본문이다.
 */
export function findDraftField(name: string): HTMLElement | null {
  // 여기서도 감춰진 앞 화면의 칸은 세지 않는다. 본문은 조회 조건 아래이므로
  // **보이는 것 중 마지막**이 본문이다.
  const found = Array.from(document.querySelectorAll<HTMLElement>(fieldSelector(name)));
  const visible = found.filter(drawn);
  return lastOf(visible.length ? visible : found);
}

/**
 * 툴바 액션을 찾는다. 대소문자를 무시한다.
 *
 * DOM 은 `RETRIEVE`·`SAVE` 로 달려 있고 카탈로그는 `retrieve`·`save` 로 적혀
 * 있다. 실제 앱에서 확인한 사실이다. 부르는 쪽이 어느 표기를 들고 있든 같은
 * 버튼을 찾아야 한다.
 */
export function findAction(id: string): HTMLElement | null {
  const wanted = id.toLowerCase();
  for (const element of Array.from(document.querySelectorAll<HTMLElement>("[data-el-action]"))) {
    if ((element.getAttribute('data-el-action') ?? '').toLowerCase() === wanted) return element;
  }
  return null;
}

export function findMenu(path: string): HTMLElement | null {
  return document.querySelector(`[data-el-menu="${path}"]`);
}

/** 앵커가 감싼 실제 입력 요소. 앵커 자신이 입력일 수도 있다. */
export type TextControl = HTMLInputElement | HTMLTextAreaElement;

export function inputOf(anchor: HTMLElement): TextControl | null {
  if (anchor instanceof HTMLInputElement || anchor instanceof HTMLTextAreaElement) return anchor;
  return anchor.querySelector('input, textarea');
}
